import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';
import { TeModal, TeContainer, TeButton, TeText, TeCallout } from '@topcon/tuicl-react';

import { useRejectCustomerSuggestion } from './customerSuggestions.mutations';
import { MigrationCandidateBusinessPartnerBody } from '../../api/generated/pm';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';
import { background, border } from '../../providers/theme/theme.index';

interface Props {
  suggestion: MigrationCandidateBusinessPartnerBody | null;
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

/**
 * Confirmation for dismissing a dealer-scope Customer Suggestion. Mirrors the
 * identity-scope `Companies/RemoveSuggestionModal`, wired to the dealer reject
 * endpoint and the `customerSuggestions` copy.
 */
function RemoveCustomerSuggestionModal({ suggestion, open, onClose }: Props) {
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { t } = useTranslation('common', { keyPrefix: 'customerSuggestions' });
  const { mutateAsync: rejectSuggestion, isPending } = useRejectCustomerSuggestion();

  const handleSubmit = async () => {
    if (!suggestion) return;
    try {
      await rejectSuggestion(suggestion.id);
      onClose(true);
    } catch (error: unknown) {
      if (!showError(error)) {
        notificationService.addNotification(
          t('suggestionRemoveFailed'),
          t('suggestionRemoveFailedDescription', { name: suggestion.name }),
          'error'
        );
      }
    }
  };

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="overlay"
      teclose={() => !isPending && onClose(false)}
      contentPadding={0}
      size="s"
      width="s"
    >
      <TeContainer backgroundColor="background.primary" padding="s" column grow gap="m">
        <TeContainer column gap="xs" padding="none">
          <TeText size={14} weight={700}>
            {t('removeSuggestionModal.title')}
          </TeText>
          <TeText size={12} color="text.secondary">
            {t('removeSuggestionModal.description')}
          </TeText>
        </TeContainer>
        <TeCallout
          status="neutral"
          grow
          icon="info"
          heading={suggestion?.name ?? ''}
          style={{
            backgroundColor: background.secondary,
            borderColor: border.primary,
            minHeight: '42px',
          }}
        />
      </TeContainer>

      <TeButton
        slot="secondary"
        text={t('removeSuggestionModal.cancel')}
        variant="neutral"
        disabled={isPending}
        teclick={() => !isPending && onClose(false)}
        size="s"
      />
      <TeButton
        slot="primary"
        text={t('removeSuggestionModal.confirm')}
        variant="secondary"
        size="s"
        loading={isPending}
        disabled={isPending}
        teclick={handleSubmit}
      />
    </TeModal>
  );
}

export default RemoveCustomerSuggestionModal;
